import { Dictionary, isUndefined, omitBy } from 'lodash';

import { LocaleToLanguageMap, LocaleType } from './translations_types';
import {
  HttpErrorUtils,
  HttpResponseBoltServer,
  HttpResponseErrorBoltServer,
} from '@bolteu/http-client-bolt-server';

export function consoleLog(...args: unknown[]) {
  if (process.env.REACT_APP_STAGE !== 'live') {
    // eslint-disable-next-line no-console -- need it here
    console.log(...args);
  }
}

export function consoleWarn(...args: unknown[]) {
  if (process.env.REACT_APP_STAGE !== 'live') {
    // eslint-disable-next-line no-console -- need it here
    console.warn(...args);
  }
}

export function consoleError(...args: unknown[]) {
  if (process.env.REACT_APP_STAGE !== 'live') {
    // eslint-disable-next-line no-console -- need it here
    console.error(...args);
  }
}

export function devWarn(...args: unknown[]) {
  consoleLog('[DEV-WARN]', ...args);
}

export function generateLocaleLanguage(locales: LocaleType[]): { [key: string]: string } {
  const localeLanguage: Record<string, string> = {};
  locales.forEach((locale) => {
    localeLanguage[locale] = LocaleToLanguageMap[locale];
  });
  return localeLanguage;
}

export function parseAsBoltError(data?: unknown): HttpResponseBoltServer.ErrorResponse | null {
  if (HttpErrorUtils.isInstanceOf(data, HttpResponseErrorBoltServer.ServerResponseError)) {
    return data.response;
  }

  return null;
}

interface ErrorResponseWithArbitraryData extends HttpResponseBoltServer.ErrorResponse {
  error_data: HttpResponseBoltServer.MissingEntityErrorResponse['error_data'];
}

/**
 * The only error which has error_data[key: string] is MissingEntityErrorResponse
 * But errors get normalized as missing only on 400 NOT_EXISTS error code from server
 * If you know you used `errorWithData()` on another error code, use this to access arbitrary keys on error_data
 */
export function parseAsBoltErrorWithExtraData(
  data?: unknown,
): ErrorResponseWithArbitraryData | null {
  return parseAsBoltError(data) as ErrorResponseWithArbitraryData | null;
}

export const objectsEqual = <T>(o1: T, o2: T): boolean => {
  function isObject(o: T) {
    return typeof o === 'object' && o !== null;
  }
  return isObject(o1) && isObject(o2) && Object.keys(o1 as object).length > 0
    ? Object.keys(o1 as object).length === Object.keys(o2 as object).length &&
        Object.keys(o1 as object).every((p) => objectsEqual(o1[p as keyof T], o2[p as keyof T]))
    : o1 === o2;
};

export const clearUndefinedKeys = <T extends object>(obj: T): Dictionary<unknown> => {
  return omitBy(obj, isUndefined);
};
